/**
 * Página pública do estudo "Por que consórcio" (/#/estudo/<id>, sem login).
 *
 * Carrega o estudo congelado do link (estudoLink.ts) e mostra em tela cheia
 * (EstudoTela), com a validade do link no rodapé.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Loader2 } from 'lucide-react';
import { COR, FONTE_PDF } from './estudoPaginas';
import { EstudoTela } from './EstudoTela';
import { carregarEstudoPublico, type EstudoPublico } from './estudoLink';

const dataCurta = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR');
};

const Aviso = ({ titulo, texto, carregando }: { titulo: string; texto?: string; carregando?: boolean }) => (
  <div
    className="flex min-h-screen flex-col items-center justify-center px-6 text-center"
    style={{ background: `radial-gradient(ellipse at top, ${COR.marinho} 0%, ${COR.noite} 60%)`, fontFamily: FONTE_PDF, color: '#fff' }}
  >
    <img src="/logos/logo-mark-white.png" alt="North" className="mb-6 h-12 opacity-90" />
    <h1 className="flex items-center gap-2 text-xl font-bold">
      {carregando && <Loader2 className="h-5 w-5 animate-spin" />}
      {titulo}
    </h1>
    {texto && (
      <p className="mt-2 max-w-sm text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
        {texto}
      </p>
    )}
  </div>
);

export const EstudoPublicoPage = () => {
  const { id = '' } = useParams();
  const [dados, setDados] = useState<EstudoPublico | null | undefined>(undefined);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    let vivo = true;
    carregarEstudoPublico(id)
      .then((d) => vivo && setDados(d))
      .catch((err) => {
        console.error('Estudo público: falha ao carregar', err);
        if (vivo) setFalhou(true);
      });
    return () => {
      vivo = false;
    };
  }, [id]);

  // Link de estudo não deve aparecer em busca.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    const titulo = document.title;
    document.title = 'Apresentação de consórcio · North';
    return () => {
      document.head.removeChild(meta);
      document.title = titulo;
    };
  }, []);

  if (falhou) return <Aviso titulo="Apresentação indisponível no momento" texto="Tente novamente em alguns minutos." />;
  if (dados === undefined) return <Aviso titulo="Carregando a apresentação" carregando />;
  if (dados === null) {
    return <Aviso titulo="Link indisponível" texto="Esta apresentação venceu ou não existe. Solicite um novo link ao especialista North." />;
  }

  const validade = dataCurta(dados.expiraEm);
  return (
    <EstudoTela
      estudo={dados.estudo}
      especialista={dados.especialista}
      rodape={validade && <> Disponível até {validade}.</>}
    />
  );
};

EstudoPublicoPage.path = '/estudo/:id';
